import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/Button';
import { TableData } from '@/components/Table';
import { DateRangeFilter } from '@/components/DatePicker';
import { FilterPopup, FilterGroup } from '@/components/FilterPopup';
import { Pagination } from '@/components/Pagination';
import { Column } from '@/types/common';
import { ActionDropdown } from '@/components/ActionDropdown';
import { ImagePreview } from '@/components/ImagePreview';
import { useAuth } from '@/contexts/AuthContext';
import AttendanceService from '@/services/api/attendance';
import { getListPitstop } from '@/services/api/pitstops';
import { showToast } from '@/components/toast/globalToast';
import { canEditAttendance, isSuperAdmin, isSupervisor } from '@/utils/access';
import {
  EditAttendanceModal,
  EditAttendanceTarget,
} from './EditAttendanceModal';

type AttendanceStatus = 'Approved' | 'Pending' | 'Rejected';

interface AttendanceRecord {
  /** UI row key — driverID from API */
  id: string;
  /** Identifier used for the approval API */
  logBookID: string;
  mitraName: string;
  mitraCode: string;
  avatarUrl?: string;
  nik: string;
  client: string;
  hub: string;
  date: string;
  clockIn: string;
  clockOut: string | null;
  duration: string | null;
  durationWarning: boolean;
  driverType: string;
  expressType: string;
  hasGps: boolean;
  gpsLat?: number | null;
  gpsLng?: number | null;
  clockOutGpsLat?: number | null;
  clockOutGpsLng?: number | null;
  /** Raw API values, kept so the edit form can prefill the real instants
   *  rather than the formatted display strings. */
  clockInAtISO: string;
  clockOutAtISO: string | null;
  timezone: string | null;
  /** Rows without a kept raw selfie cannot be corrected — see the API. */
  editable: boolean;
  clockOutReason?: string | null;
  selfieUrl?: string | null;
  status: AttendanceStatus;
  statusUpdatedBy: string | null;
  statusUpdatedAt: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toUiStatus(apiStatus: string): AttendanceStatus {
  switch (apiStatus?.toUpperCase()) {
    case 'APPROVE':
    case 'APPROVED':
      return 'Approved';
    case 'REJECT':
    case 'REJECTED':
      return 'Rejected';
    default:
      return 'Pending';
  }
}

/** Extract HH:MM (local) from an ISO datetime string, or return null. */
function isoToTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const TZ_LABELS: Record<string, string> = {
  'Asia/Jakarta': 'WIB',
  'Asia/Makassar': 'WITA',
  'Asia/Jayapura': 'WIT',
};

/** "09:00 (WIB)" — the time on the row's own clock (the clock-in pitstop's
 *  timezone); rows without one show as WIB. Unknown zones fall back to WIB. */
function isoToTimeWithZone(
  iso: string | null | undefined,
  timezone: string | null | undefined
): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  let tz = timezone ?? 'Asia/Jakarta';
  let time: string;
  try {
    time = d.toLocaleTimeString('id-ID', { ...opts, timeZone: tz });
  } catch {
    tz = 'Asia/Jakarta';
    time = d.toLocaleTimeString('id-ID', { ...opts, timeZone: tz });
  }
  return `${time} (${TZ_LABELS[tz] ?? tz})`;
}

/** Extract YYYY-MM-DD (local) from an ISO datetime string. */
function isoToDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Format ISO datetime as "Senin, 16 Mar 2026" (Indonesian locale) on the
 *  row's own clock (the clock-in pitstop's timezone), so the Date column
 *  agrees with the Clock In/Out times next to it. Rows without a zone, or
 *  with an unknown one, fall back to WIB — same rule as isoToTimeWithZone. */
function isoToLongDate(
  iso: string | null | undefined,
  timezone: string | null | undefined
): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };
  try {
    return d.toLocaleDateString('id-ID', {
      ...opts,
      timeZone: timezone ?? 'Asia/Jakarta',
    });
  } catch {
    return d.toLocaleDateString('id-ID', { ...opts, timeZone: 'Asia/Jakarta' });
  }
}

/** Compute HH:MM duration between two ISO strings, returns null if either is missing. */
function computeDuration(inIso: string, outIso: string | null): string | null {
  if (!outIso) return null;
  const diff = new Date(outIso).getTime() - new Date(inIso).getTime();
  if (diff < 0) return null;
  const totalMins = Math.floor(diff / 60000);
  const h = Math.floor(totalMins / 60);
  const min = totalMins % 60;
  return `${h}h ${String(min).padStart(2, '0')}m`;
}

/** Duration warning threshold: > 12 hours */
const DURATION_WARN_HOURS = 12;
function isDurationOver(inIso: string, outIso: string | null): boolean {
  if (!outIso) return false;
  const diff = new Date(outIso).getTime() - new Date(inIso).getTime();
  return diff > DURATION_WARN_HOURS * 3600 * 1000;
}

function formatDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const BASE_FILTER_GROUPS: FilterGroup[] = [
  {
    title: 'Status',
    key: 'status',
    icon: 'ri-checkbox-circle-line',
    options: [
      { key: 'approved', label: 'Approved', value: 'APPROVE' },
      { key: 'pending', label: 'Pending', value: 'PENDING' },
      { key: 'rejected', label: 'Rejected', value: 'REJECT' },
    ],
    multiple: true,
  },
  {
    title: 'Talent Type',
    key: 'talentType',
    icon: 'ri-user-line',
    options: [
      { key: 'rider', label: 'Rider', value: 'RIDER' },
      { key: 'driver', label: 'Driver', value: 'DRIVER' },
      { key: 'warehouse', label: 'Warehouse Worker', value: 'WAREHOUSE' },
    ],
    multiple: true,
  },
  {
    title: 'Duration',
    key: 'durationType',
    icon: 'ri-timer-line',
    options: [
      { key: 'normal', label: 'Normal Duration ', value: 'NORMAL_DURATION' },
      { key: 'over', label: 'Over Duration ', value: 'OVER_DURATION' },
    ],
    multiple: true,
  },
];

const PAGE_SIZE_DEFAULT = 10;

const STATUS_STYLE: Record<AttendanceStatus, string> = {
  Approved: 'text-green-600',
  Pending: 'text-yellow-500',
  Rejected: 'text-red-500',
};

const STATUS_ICON: Record<AttendanceStatus, string> = {
  Approved: 'ri-checkbox-circle-fill',
  Pending: 'ri-error-warning-fill',
  Rejected: 'ri-close-circle-fill',
};

function StatusBadge({ status }: { status: AttendanceStatus }) {
  return (
    <span
      className={`flex items-center gap-1 text-sm font-medium ${STATUS_STYLE[status]}`}
    >
      <i className={`${STATUS_ICON[status]} text-[14px]`} />
      {status}
    </span>
  );
}

function HubCell({ value }: { value: unknown }) {
  const [expanded, setExpanded] = React.useState(false);
  const hubs = (value as string)
    ?.split(',')
    .map((h) => h.trim())
    .filter(Boolean);
  if (!hubs?.length) return <span className="text-sm text-gray-400">-</span>;
  const visible = expanded ? hubs : hubs.slice(0, 3);
  const hidden = hubs.length - 3;
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((hub, i) => (
        <span
          key={i}
          className="inline-block px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-md whitespace-nowrap"
        >
          {hub}
        </span>
      ))}
      {hubs.length > 3 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="inline-block px-2 py-0.5 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md whitespace-nowrap hover:bg-purple-100 transition-colors"
        >
          {expanded ? 'Show less' : `+${hidden} more`}
        </button>
      )}
    </div>
  );
}

function ClockDot({ color }: { color: 'green' | 'red' }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
        color === 'green' ? 'bg-green-500' : 'bg-red-500'
      }`}
    />
  );
}

const buildColumns = (
  onAction: (logBookID: string, action: 'Approved' | 'Rejected') => void,
  openDropdownId: string | null,
  setOpenDropdownId: (id: string | null) => void,
  openSelfieId: string | null,
  setOpenSelfieId: (id: string | null) => void,
  canEdit: boolean,
  onEdit: (record: AttendanceRecord) => void
): Column<AttendanceRecord>[] => [
  {
    key: 'mitraName',
    title: 'Mitra Name',
    sortable: true,
    render: (_value, record) => {
      const avatarSrc =
        record.avatarUrl ||
        `https://ui-avatars.com/api/?name=${encodeURIComponent(record.mitraName)}&size=32&background=e9d5ff&color=7c3aed&bold=true&rounded=true`;
      return (
        <div className="flex items-center gap-3">
          <div className="shrink-0 w-8 h-8 rounded-full overflow-hidden">
            <ImagePreview
              src={avatarSrc}
              alt={record.mitraName}
              className="w-8 h-8 rounded-full object-cover"
            />
          </div>
          <div
            // onClick={() => {
            //   const backOfficeUrl =
            //     process.env.REACT_APP_BACK_OFFICE_URL ||
            //     'https://back-office.dashelectric.co';
            //   window.open(`${backOfficeUrl}/driver/${record.id}`, '_blank');
            // }}
            className="group text-left min-w-0"
          >
            <p className="text-sm font-medium text-gray-900 leading-tight transition-colors">
              {record.mitraName}
            </p>
            <p className="text-xs text-gray-400 leading-tight">
              {record.mitraCode}
            </p>
          </div>
        </div>
      );
    },
  },
  { key: 'nik', title: 'NIK', sortable: true },
  {
    key: 'hub',
    title: 'Hub',
    sortable: true,
    render: (value) => <HubCell value={value} />,
  },
  {
    key: 'driverType',
    title: 'Talent Type',
    sortable: true,
    render: (value) => {
      const labels: Record<string, string> = {
        RIDER: 'Rider',
        DRIVER: 'Driver',
        WAREHOUSE: 'Warehouse Worker',
        CANVASSER: 'Canvasser',
      };
      const label = labels[(value as string)?.toUpperCase()] ?? value;
      return <span className="text-sm text-gray-700">{label}</span>;
    },
  },
  {
    key: 'date',
    title: 'Date',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-700 whitespace-nowrap">{value}</span>
    ),
  },
  {
    key: 'clockIn',
    title: 'Clock In',
    sortable: true,
    render: (value) => (
      <span className="flex items-center text-sm text-gray-700 whitespace-nowrap">
        <ClockDot color="green" />
        {value}
      </span>
    ),
  },
  {
    key: 'hasGps',
    title: 'GPS ClockIn Location / Selfie',
    render: (_value, record) => (
      <div className="flex items-center gap-2">
        {record.hasGps && record.gpsLat != null && record.gpsLng != null ? (
          <button
            title="Open in Google Maps"
            onClick={() =>
              window.open(
                `https://www.google.com/maps?q=${record.gpsLat},${record.gpsLng}`,
                '_blank'
              )
            }
            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-full text-gray-500 hover:text-purple-600 hover:border-purple-400 transition-colors"
          >
            <i className="ri-map-2-line text-[18px]" />
          </button>
        ) : (
          <i className="ri-map-2-line text-[18px] text-gray-300" />
        )}
        {record.selfieUrl ? (
          <>
            <button
              title="View selfie"
              onClick={() => setOpenSelfieId(record.id)}
              className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-full text-gray-500 hover:text-purple-600 hover:border-purple-400 transition-colors"
            >
              <i className="ri-image-line text-[18px]" />
            </button>
            <ImagePreview
              src={record.selfieUrl}
              alt="Selfie"
              open={openSelfieId === record.id}
              onOpenChange={(v) => !v && setOpenSelfieId(null)}
            />
          </>
        ) : (
          <i className="ri-image-line text-[18px] text-gray-300" />
        )}
      </div>
    ),
  },
  {
    key: 'clockOut',
    title: 'Clock Out',
    sortable: true,
    render: (value, record) =>
      value ? (
        <span className="flex items-center gap-1 text-sm text-gray-700 whitespace-nowrap">
          <ClockDot color="red" />
          {value}
          {value && record.clockOutGpsLat == null && (
            <span className="relative group cursor-default flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4 text-yellow-400"
              >
                <path
                  fillRule="evenodd"
                  d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
                Auto Clocked Out By System
                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
              </span>
            </span>
          )}
        </span>
      ) : (
        <span className="text-sm text-gray-400 italic">Ongoing</span>
      ),
  },
  {
    key: 'clockOutGpsLat',
    title: 'GPS Clock Out',
    render: (_value, record) =>
      record.clockOutGpsLat != null && record.clockOutGpsLng != null ? (
        <button
          title="Open in Google Maps"
          onClick={() =>
            window.open(
              `https://www.google.com/maps?q=${record.clockOutGpsLat},${record.clockOutGpsLng}`,
              '_blank'
            )
          }
          className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-full text-gray-500 hover:text-purple-600 hover:border-purple-400 transition-colors"
        >
          <i className="ri-map-2-line text-[18px]" />
        </button>
      ) : (
        <i className="ri-map-2-line text-[18px] text-gray-300" />
      ),
  },
  {
    key: 'duration',
    title: 'Duration',
    sortable: true,
    render: (_value, record) =>
      record.duration ? (
        <span
          className={`flex items-center gap-1 text-sm whitespace-nowrap ${record.durationWarning ? 'text-yellow-500' : 'text-gray-700'}`}
        >
          {record.durationWarning && (
            <i className="ri-error-warning-fill text-[14px]" />
          )}
          {record.duration}
        </span>
      ) : (
        <span className="text-sm text-gray-400">-</span>
      ),
  },

  {
    key: 'status',
    title: 'Status',
    sortable: true,
    render: (value) => <StatusBadge status={value as AttendanceStatus} />,
  },
  {
    key: 'statusUpdatedBy',
    title: 'Status Updated By',
    render: (_value, record) =>
      record.statusUpdatedBy ? (
        <div>
          <p className="text-sm text-gray-700">{record.statusUpdatedBy}</p>
          <p className="text-xs text-gray-400">
            {isoToDate(record.statusUpdatedAt)}{' '}
            {isoToTime(record.statusUpdatedAt) ?? ''}
          </p>
        </div>
      ) : (
        <span className="text-sm text-gray-400">-</span>
      ),
  },
  {
    key: 'id',
    title: '',
    render: (_value, record) => {
      if (record.status !== 'Pending') return null;
      // Approving needs a finished shift; correcting the clock-in does not,
      // so an ongoing row still offers Edit on its own.
      const actions = [];
      if (canEdit && record.editable) {
        actions.push({
          key: 'edit',
          label: 'Edit Attendance',
          icon: 'ri-edit-line',
          className: 'text-gray-700',
          onClick: () => {
            onEdit(record);
            setOpenDropdownId(null);
          },
        });
      }
      if (!record.clockOut) {
        return actions.length ? (
          <ActionDropdown
            record={record}
            isOpen={openDropdownId === record.id}
            onToggle={() =>
              setOpenDropdownId(openDropdownId === record.id ? null : record.id)
            }
            actions={actions}
          />
        ) : null;
      }
      return (
        <ActionDropdown
          record={record}
          isOpen={openDropdownId === record.id}
          onToggle={() =>
            setOpenDropdownId(openDropdownId === record.id ? null : record.id)
          }
          actions={[
            ...actions,
            {
              key: 'approve',
              label: 'Approve',
              icon: 'ri-checkbox-circle-line',
              className: 'text-green-600',
              onClick: () => {
                onAction(record.logBookID, 'Approved');
                setOpenDropdownId(null);
              },
            },
            {
              key: 'reject',
              label: 'Reject',
              icon: 'ri-close-circle-line',
              className: 'text-red-500',
              onClick: () => {
                onAction(record.logBookID, 'Rejected');
                setOpenDropdownId(null);
              },
            },
          ]}
        />
      );
    },
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

const AttendancePage: React.FC = () => {
  const { user } = useAuth();

  // Super admin wins for a user holding both, so they get the wider
  // pitstop list below.
  const isClientSuperAdmin = isSuperAdmin(user);
  const isClientSupervisor = !isClientSuperAdmin && isSupervisor(user);
  // Both roles scope the table to their own provider
  const supervisorProviderId =
    isClientSupervisor || isClientSuperAdmin
      ? (user?.providerId ?? null)
      : null;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [pitstopFilterOptions, setPitstopFilterOptions] = useState<
    FilterGroup['options']
  >([]);
  const [dateRange, setDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: new Date(
      new Date().setMonth(new Date().getMonth() - 3, new Date().getDate())
    ),
    to: new Date(new Date().setHours(0, 0, 0, 0)),
  });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [openSelfieId, setOpenSelfieId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<EditAttendanceTarget | null>(
    null
  );
  const canEdit = canEditAttendance(user);

  // Derive pitstop options:
  // - client_supervisor  → from profile.providerPitstops (already scoped)
  // - client_super_admin / client_internal_super_admin → profile.providerPitstops,
  //   or every pitstop of their providerId from the API when none are assigned
  useEffect(() => {
    if (isClientSupervisor) {
      const pitstops = user?.providerPitstops ?? [];
      setPitstopFilterOptions(
        pitstops.map((p) => ({
          key: String(p.id),
          label: p.name,
          value: String(p.id),
        }))
      );
    } else if (isClientSuperAdmin) {
      const profilePitstops = user?.providerPitstops ?? [];
      if (profilePitstops.length > 0) {
        setPitstopFilterOptions(
          profilePitstops.map((p) => ({
            key: String(p.id),
            label: p.name,
            value: String(p.id),
          }))
        );
      } else if (supervisorProviderId != null) {
        getListPitstop({ size: '1000', providerID: supervisorProviderId })
          .then((res) => {
            setPitstopFilterOptions(
              res.data.map((p) => ({
                key: String(p.id),
                label: p.name,
                value: String(p.id),
              }))
            );
          })
          .catch(console.error);
      }
    }
  }, [
    isClientSupervisor,
    isClientSuperAdmin,
    supervisorProviderId,
    user?.providerPitstops,
  ]);

  // Build filter groups — add Pitstop group for scoped roles
  const filterGroups: FilterGroup[] =
    (isClientSupervisor || isClientSuperAdmin) &&
    pitstopFilterOptions.length > 0
      ? [
          ...BASE_FILTER_GROUPS,
          {
            title: 'Pitstop',
            key: 'pitstop',
            icon: 'ri-map-pin-line',
            options: pitstopFilterOptions,
            multiple: true,
          },
        ]
      : BASE_FILTER_GROUPS;

  // ── Data fetching ────────────────────────────────────────────────────────

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AttendanceService.getList({
        startDate: formatDate(dateRange.from),
        endDate: formatDate(dateRange.to),
        search: debouncedSearch || undefined,
        approvalTypes: activeFilters.status?.length
          ? (activeFilters.status as string[]).join(',')
          : undefined,
        driverTypes: activeFilters.talentType?.length
          ? (activeFilters.talentType as string[]).join(',')
          : undefined,
        durationTypes: activeFilters.durationType?.length
          ? (activeFilters.durationType as string[]).join(',')
          : undefined,
        // supervisor / super admin: always scope to their provider
        providerIDs:
          supervisorProviderId != null ? [supervisorProviderId] : undefined,
        // pitstop filter (supervisor / super admin only)
        pitstopIDs: activeFilters.pitstop?.length
          ? activeFilters.pitstop
          : undefined,
        page: currentPage,
        size: pageSize,
      });

      const mapped: AttendanceRecord[] = response.data.map((item) => ({
        id: item.logBookID, // unique per row
        logBookID: item.logBookID,
        mitraName: item.driverName,
        mitraCode: item.driverCode,
        avatarUrl: item.driverSelfieImageUrl ?? undefined,
        nik: item.driverNIK,
        client: item.clientNames,
        hub: item.hubNames,
        date: isoToLongDate(item.clockInAt, item.clockInAtPitstopTimezone),
        clockInAtISO: item.clockInAt,
        editable: item.editable ?? false,
        clockOutAtISO: item.clockOutAt,
        timezone: item.clockInAtPitstopTimezone ?? null,
        clockIn:
          isoToTimeWithZone(item.clockInAt, item.clockInAtPitstopTimezone) ??
          '-',
        clockOut: isoToTimeWithZone(
          item.clockOutAt,
          item.clockInAtPitstopTimezone
        ),
        duration: computeDuration(item.clockInAt, item.clockOutAt),
        durationWarning: isDurationOver(item.clockInAt, item.clockOutAt),
        driverType: item.driverType,
        expressType: item.expressType,
        hasGps: item.latitude != null && item.longitude != null,
        gpsLat: item.latitude,
        gpsLng: item.longitude,
        clockOutGpsLat: item.clockOutLatitude,
        clockOutGpsLng: item.clockOutLongitude,
        clockOutReason: item.clockOutReason,
        selfieUrl: item.selfieImageUrl,
        status: toUiStatus(item.approvalType),
        statusUpdatedBy: item.approvalFullName ?? item.approvalEmail,
        statusUpdatedAt: item.approvalAt,
      }));

      setRecords(mapped);
      setTotalItems(Number(response.pagination.total));
    } catch (err) {
      console.error('Failed to fetch attendance:', err);
    } finally {
      setLoading(false);
    }
  }, [
    dateRange,
    debouncedSearch,
    activeFilters,
    currentPage,
    pageSize,
    supervisorProviderId,
  ]);

  // Debounce search input
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const handleAction = async (
    logBookID: string,
    action: 'Approved' | 'Rejected'
  ) => {
    if (!user) return;
    try {
      const result = await AttendanceService.bulkApproval({
        approvalUserID: String(user.id ?? user.uid ?? ''),
        approvalEmail: user.email ?? '',
        approvalFullName: user.name ?? user.displayName ?? '',
        records: [
          {
            logBookID,
            approvalType: action === 'Approved' ? 'APPROVE' : 'REJECT',
          },
        ],
      });
      if (result.totalSuccess > 0 && result.totalError === 0) {
        showToast(
          'success',
          'Success',
          `Attendance ${action.toLowerCase()} successfully.`
        );
      } else if (result.totalSuccess > 0 && result.totalError > 0) {
        const messages = result.error.map((e) => e.message).join(', ');
        showToast(
          'warning',
          'Partially Successful',
          `Attendance ${action.toLowerCase()} successfully, but ${result.totalError} failed: ${messages}`
        );
      } else if (result.totalError > 0) {
        const messages = result.error.map((e) => e.message).join(', ');
        showToast('error', 'Approval Failed', messages);
      }
      await fetchRecords();
    } catch (err) {
      console.error('Approval failed:', err);
      showToast('error', 'Error', 'Failed to process approval.');
    }
  };

  const columns = buildColumns(
    handleAction,
    openDropdownId,
    setOpenDropdownId,
    openSelfieId,
    setOpenSelfieId,
    canEdit,
    (record) =>
      setEditTarget({
        logBookID: record.logBookID,
        mitraName: record.mitraName,
        mitraCode: record.mitraCode,
        hub: record.hub,
        date: record.date,
        clockInAt: record.clockInAtISO,
        clockOutAt: record.clockOutAtISO,
        timezone: record.timezone,
      })
  );

  const handleEditSubmit = async (payload: {
    clockInAt?: string;
    clockOutAt?: string | null;
    editReason: string;
  }) => {
    if (!editTarget || !user) return;
    try {
      const result = await AttendanceService.edit(editTarget.logBookID, {
        ...payload,
        editedBy: user.email ?? '',
      });
      showToast(
        'success',
        'Attendance Updated',
        result.selfieRestamped
          ? 'Jam berhasil diubah. Stempel waktu di selfie lagi digenerate ulang.'
          : 'Jam berhasil diubah.'
      );
      setEditTarget(null);
      await fetchRecords();
    } catch (err: any) {
      showToast(
        'error',
        'Gagal Mengubah Attendance',
        err?.response?.data?.message ?? 'Coba lagi sebentar.'
      );
    }
  };

  const handleBulkAction = async (action: 'Approved' | 'Rejected') => {
    if (!user) return;
    const approvalRecords = records
      .filter((r) => selectedRows.includes(r.id))
      .map((r) => ({
        logBookID: r.logBookID,
        approvalType: (action === 'Approved' ? 'APPROVE' : 'REJECT') as
          | 'APPROVE'
          | 'REJECT',
      }));
    if (!approvalRecords.length) return;
    try {
      const result = await AttendanceService.bulkApproval({
        approvalUserID: String(user.uid ?? user.id ?? ''),
        approvalEmail: user.email ?? '',
        approvalFullName: user.name ?? '',
        records: approvalRecords,
      });
      if (result.totalSuccess > 0 && result.totalError === 0) {
        showToast(
          'success',
          'Bulk Action Successful',
          `${result.totalSuccess} record${result.totalSuccess > 1 ? 's' : ''} ${action.toLowerCase()} successfully.`
        );
      } else if (result.totalSuccess > 0 && result.totalError > 0) {
        showToast(
          'warning',
          'Partially Successful',
          `${result.totalSuccess} record${result.totalSuccess > 1 ? 's' : ''} ${action.toLowerCase()} successfully, ${result.totalError} failed.`
        );
      } else if (result.totalError > 0) {
        showToast(
          'error',
          'Bulk Action Failed',
          `${result.totalError} record${result.totalError > 1 ? 's' : ''} could not be processed.`
        );
      }
      setSelectedRows([]);
      await fetchRecords();
    } catch (err) {
      console.error('Bulk approval failed:', err);
      showToast('error', 'Error', 'Failed to process bulk approval.');
    }
  };

  const handleDownloadCSV = async () => {
    setDownloading(true);
    try {
      const blob = await AttendanceService.download({
        approvalTypes: activeFilters.status?.length
          ? activeFilters.status
          : undefined,
        driverTypes: activeFilters.talentType?.length
          ? activeFilters.talentType
          : undefined,
        durationTypes: activeFilters.durationType?.length
          ? activeFilters.durationType
          : undefined,
        providerIDs:
          supervisorProviderId != null ? [supervisorProviderId] : undefined,
        pitstopIDs: activeFilters.pitstop?.length
          ? (activeFilters.pitstop as string[]).map(Number)
          : undefined,
        startDate: formatDate(dateRange.from),
        endDate: formatDate(dateRange.to),
      });
      triggerBlobDownload(blob, 'attendance.csv');
    } catch (err) {
      console.error('Download failed:', err);
      showToast(
        'error',
        'Download Failed',
        'Failed to download attendance data.'
      );
    } finally {
      setDownloading(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────

  const activeFilterCount = Object.values(activeFilters).filter(
    (v) =>
      v !== undefined &&
      v !== null &&
      v !== '' &&
      (Array.isArray(v) ? v.length > 0 : true)
  ).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="bg-white p-6 max-w-full mx-auto space-y-5">
      {/* Bulk action floating bar */}
      {selectedRows.length > 0 && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-max z-50 flex items-center gap-2 bg-white border border-gray-200 shadow-xl rounded-2xl px-3 py-2 sm:px-4 sm:py-2.5 animate-in fade-in slide-in-from-bottom-2">
          <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700 pr-2 border-r border-gray-200 shrink-0">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">
              {selectedRows.length}
            </span>
            <span className="hidden sm:inline">Selected</span>
          </span>
          <button
            onClick={() => handleBulkAction('Rejected')}
            className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
          >
            <i className="ri-close-circle-line text-[16px]" />
            <span>Reject Attendance</span>
          </button>
          <button
            onClick={() => handleBulkAction('Approved')}
            className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-green-600 hover:bg-green-50 transition-colors"
          >
            <i className="ri-checkbox-circle-line text-[16px]" />
            <span>Approve Attendance</span>
          </button>
          <button
            onClick={() => setSelectedRows([])}
            className="shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title="Dismiss"
          >
            <i className="ri-close-line text-[18px]" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Attendance</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Monitor and audit internal attendance logs, selfie verifications,
            and location data.
          </p>
        </div>
        <Button
          variant="primary"
          size="medium"
          onClick={handleDownloadCSV}
          disabled={downloading}
          className="w-full sm:w-auto shrink-0 flex items-center justify-center gap-2"
        >
          {downloading ? (
            <i className="ri-loader-4-line animate-spin text-[16px]" />
          ) : (
            <i className="ri-download-2-line text-[16px]" />
          )}
          {downloading ? 'Downloading...' : 'Download as CSV'}
        </Button>
      </div>

      {/* Filters row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        <div className="w-full sm:w-auto">
          <DateRangeFilter
            onDateRangeChange={(range) => {
              setDateRange(range);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="hidden sm:flex flex-1" />

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[16px]" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search..."
              className="h-9 pl-9 pr-4 w-full sm:w-64 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="relative shrink-0">
            <button
              onClick={() => setFilterOpen((v) => !v)}
              className="flex items-center gap-2 h-9 px-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <i className="ri-equalizer-line text-[16px] text-gray-400" />
              <span className="hidden sm:inline">Filter</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-purple-600 text-white text-[10px] font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <FilterPopup
              isOpen={filterOpen}
              onClose={() => setFilterOpen(false)}
              onApply={(filters) => {
                setActiveFilters(filters);
                setCurrentPage(1);
                setFilterOpen(false);
              }}
              onClear={() => {
                setActiveFilters({});
                setCurrentPage(1);
                setFilterOpen(false);
              }}
              filterGroups={filterGroups}
              currentFilters={activeFilters}
            />
          </div>
        </div>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div className="flex items-center justify-center py-6">
          <i className="ri-loader-4-line animate-spin text-2xl text-purple-500" />
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <TableData<AttendanceRecord>
            data={records}
            columns={columns}
            selection={{
              selectedKeys: selectedRows,
              onSelectionChange: setSelectedRows,
              getRowKey: (row) => row.id,
              isRowSelectable: (row) =>
                !!row.clockOut && row.status === 'Pending',
            }}
            emptyState={
              <div className="text-center">
                <div className="w-32 h-32 mx-auto mb-4 opacity-50">
                  <img
                    src="/images/no-results.png"
                    alt="No attendance"
                    className="w-full h-full"
                  />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Attendance to Display Yet
                </h3>
                <p className="text-sm text-gray-500 max-w-xs mx-auto">
                  Attendance logs will appear in this list once workers begin
                  recording their clock in/out attempts via the Dash mobile app.
                </p>
              </div>
            }
          />
        </div>
      )}

      <EditAttendanceModal
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSubmit={handleEditSubmit}
      />

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default AttendancePage;
